import { Radio } from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import userAPI from "../api/userAPI";
import swalDialog from "../helpers/swalErrorPopup";
import { getSimulatedCreditCard } from "../testSimulation";
import MastercardNew from "../img/svg/Mastercard_new.svg";
import CouponIcon from "../img/svg/coupon.svg";
import MultipleEntryPassCard from "../img/svg/multiple-entry-pass-card.svg";
import MultipleEntryPassCardOvdimBari from "../img/svg/multiple-entry-pass-card-ovdimbari.svg";
import SecondMultipleEntryPassCardMaof from "../img/svg/multiple-entry-pass-card-maof.png";
import { isLoadChanger } from "../store/mainSlice";
import { isPartnerClalit, isPartnerOvdimBari, isPartnerMaoflaamit, selectPartner } from "../store/partnerStore";
import { isV2Enabled, V2_SCREEN_KEYS } from "../v2/registry";
import { getPartnerAsset } from "../v2/theme/partnerAssets";
import ConfirmDialog from "../v2/components/ConfirmDialog";
import PassPurchaseV2 from "../v2/screens/passPurchase/PassPurchase";
import clubAPI from "../api/clubAPI";
import { updateAllAppParams } from "../store/appParamSlice";
import {
  addNewMultipleEntryPass,
  checkCanPurchasePass,
  fetchMultipleEntryPasses,
  hasActiveMultipleEntryPassSelector,
  selectCanPurchaseChecked,
  selectCanPurchaseNewPass,
} from "../store/multipleEntryPassSlice";
import "../styles/multipleEntryPassPurchase.scss";

const MultipleEntryPassPurchase = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();

  const hasActiveMultipleEntryPass = useSelector(hasActiveMultipleEntryPassSelector);
  const canPurchaseNewPass = useSelector(selectCanPurchaseNewPass);
  const canPurchaseChecked = useSelector(selectCanPurchaseChecked);

  const userParams = useSelector((state) => state.userSlice.userData);
  const mainUserParams = useSelector((state) => state.userSlice.mainUserData);
  const partner = useSelector(selectPartner);
  const isClalit = useSelector(isPartnerClalit);
  const isOvdimBari = useSelector(isPartnerOvdimBari);
  const isMaoflaamit = useSelector(isPartnerMaoflaamit);

  // Local state
  const [last4Digit, setLast4Digit] = useState("");
  const [hasCard, setHasCard] = useState(null); // null = card status not resolved yet
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("multipleEntryPass");
  const [selectedPassPlanId, setSelectedPassPlanId] = useState(3);

  const showCardRow = !isMaoflaamit && (!isClalit || (isClalit && !hasActiveMultipleEntryPass));

  // After a successful purchase the eligibility re-check flips to "no" while we
  // navigate away — the block dialog must not flash at a user who just bought.
  const purchaseSucceededRef = useRef(false);

  useEffect(() => {
    checkIsHasCreditCard();
  }, []);

  // Deep links can land here without the home screen ever running the
  // eligibility check; the thunk dedupes in-flight calls.
  useEffect(() => {
    if (!mainUserParams?.Token) return;
    dispatch(
      checkCanPurchasePass({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
      }),
    );
  }, [mainUserParams?.Token]);

  const checkIsHasCreditCard = async () => {
    // TEMP-TEST (remove after testing) — see SIMULATED_HAS_CREDIT_CARD in testSimulation.js.
    const simulatedCard = getSimulatedCreditCard();
    if (simulatedCard) {
      setLast4Digit(simulatedCard.Last4Digit);
      setHasCard(simulatedCard.HasCreditCard);
      if (simulatedCard.HasCreditCard && simulatedCard.Last4Digit && !isMaoflaamit) {
        setSelectedPaymentMethod("creditCard");
      }
      return;
    }

    try {
      dispatch(isLoadChanger(true));

      let isHaveCreditCard = await userAPI.isClientHaveCreditCard({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
      });

      if (isHaveCreditCard.data.Error != 0) {
        throw {
          code: isHaveCreditCard.data.Error,
          mess: isHaveCreditCard.data.Message,
        };
      }

      const jsonData = JSON.parse(isHaveCreditCard.data.Data);
      setLast4Digit(jsonData.Last4Digit);
      setHasCard(!!jsonData.HasCreditCard);

      if (jsonData.HasCreditCard && jsonData.Last4Digit && !isMaoflaamit) {
        setSelectedPaymentMethod("creditCard");
      }

      dispatch(isLoadChanger(false));
    } catch (err) {
      dispatch(isLoadChanger(false));
      swalDialog(err);
    }
  };

  const handlePaymentMethodChange = (method) => {
    setSelectedPaymentMethod(method);
  };

  const handlePayment = async () => {
    if (showCardRow && hasCard === false) {
      navigate("/addCard");
      return;
    }
    try {
      dispatch(isLoadChanger(true));
      const result = await userAPI.purchaseMultipleEntryPass({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
        passPlanId: isMaoflaamit ? selectedPassPlanId : isOvdimBari ? 2 : 1,
      });
      purchaseSucceededRef.current = true;
      dispatch(addNewMultipleEntryPass(result));
      // The optimistic push above has the PurchaseFreeFitWebPass response shape;
      // reconcile with the GetAllFreefitWebPass shape the pass cards render from.
      dispatch(
        fetchMultipleEntryPasses({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
          force: true,
        }),
      );
      dispatch(
        checkCanPurchasePass({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        }),
      );

      if (isOvdimBari || isMaoflaamit) {
        try {
          const param = await clubAPI.getWebFullData({
            binID: userParams.BinID,
            token: mainUserParams.Token,
            id: mainUserParams.ID,
            phone: mainUserParams.Phone,
          });
          if (param.data.Error !== 0) {
            throw { code: param.data.Error, mess: param.data.Message };
          }
          dispatch(updateAllAppParams(JSON.parse(param.data.Data)));
        } catch (err) {
          swalDialog(err);
        }
      }

      dispatch(isLoadChanger(false));

      navigate("/multipleEntryPass", { replace: true });
    } catch (error) {
      dispatch(isLoadChanger(false));
      swalDialog(error);
      return;
    }
  };

  const calculatePrice = () => {
    if (isMaoflaamit) {
      if (selectedPaymentMethod === "creditCard") return location.state?.price ?? 0;
      return selectedPassPlanId === 3 ? 150 : 160;
    }
    if (isOvdimBari) return 160;
    return 90;
  };

  const getExpirationDate = () => {
    return moment().add(5, "year").format("DD/MM/YYYY");
  };

  // Server said this user may not buy a pass (entry points only hide the buttons —
  // a deep link still reaches this page). Unclosable: the only ways out are home
  // or support.
  const showNotEligible = canPurchaseChecked && canPurchaseNewPass === false && !purchaseSucceededRef.current;

  const notEligibleDialog = (
    <ConfirmDialog
      isOpen={showNotEligible}
      dismissible={false}
      title={t("PASS_NOT_ELIGIBLE_TITLE")}
      body={t("PASS_NOT_ELIGIBLE_BODY")}
      primaryText={t("ERROR_POPUP_CONFIRM")}
      onPrimary={() => navigate("/", { replace: true })}
      secondaryText={t("ERROR_POPUP_WHATSAPP")}
      onSecondary={() => window.open("https://wa.me/972559458809", "_blank")}
    />
  );

  if (isV2Enabled(V2_SCREEN_KEYS.MULTIPLE_ENTRY_PASS_PURCHASE, partner)) {
    return (
      <>
      <PassPurchaseV2
        passImg={getPartnerAsset(partner, "passCardImg")}
        title={isMaoflaamit ? `כרטיסיית ${selectedPassPlanId} כניסות` : t("MULTIPLE_ENTRY_PASS_DETAILS")}
        subtitle={t("MULTIPLE_ENTRY_PASS_ALL_UNIVERSAL_LOCATIONS")}
        expiresText={t("EXPIRES_ON", { date: getExpirationDate() })}
        showCardRow={showCardRow}
        hasCard={hasCard}
        last4Digit={last4Digit}
        total={calculatePrice()}
        onPay={handlePayment}
        onPaymentRowClick={() => navigate(hasCard ? "/card-details" : "/addCard")}
      />
      {notEligibleDialog}
      </>
    );
  }

  return (
    <div className="main-page-container">
      {notEligibleDialog}
      <div className={`multiple-entry-pass-purchase${isOvdimBari  ? " multiple-entry-pass-purchase--ovdim-bari" : isMaoflaamit ? " multiple-entry-pass-purchase--maof" : ""}`}>
        <div>
          <img src={isClalit ? MultipleEntryPassCard : isMaoflaamit ? SecondMultipleEntryPassCardMaof : MultipleEntryPassCardOvdimBari} />
        </div>
        <div className="multiple-entry-pass-purchase__bottom-popup">
          <div className="multiple-entry-pass-purchase__bottom-popup__content">
            <div className={isMaoflaamit ? "popup-content-wrapper-maof" : "popup-content-wrapper"}>
              <div>
                <div className="mobile-popup-header">
                  <p className="mobile-popup-header-text">
                    {isMaoflaamit ? `כרטיסיית ${selectedPassPlanId} כניסות` : t("MULTIPLE_ENTRY_PASS_DETAILS")}
                  </p>
                </div>
                <div className={"mobile-popup-header-info"}>
                  <p style={{ direction: "rtl" }}>{t("MULTIPLE_ENTRY_PASS_ALL_UNIVERSAL_LOCATIONS")}</p>
                  <>{t("EXPIRES_ON", { date: getExpirationDate() })}</>
                </div>
              </div>
              <div className="mobile-popup-content-wrapper">
                <div className={isMaoflaamit ? "mobile-popup-text-content-container-maof" : "mobile-popup-text-content-container"}>
                  {isMaoflaamit && (
                    <>
                      <div className="mobile-popup-payment-method-container">
                        <div className="mobile-popup-card-number">
                          <img src={CouponIcon} alt="Pass" style={{ width: "28px", height: "28px" }} />
                        </div>
                        <div className="radio-btn-container">
                          <p className="credit-card">כרטיסיית 3 כניסות</p>
                          <Radio
                            checked={selectedPassPlanId === 3 && selectedPaymentMethod !== "creditCard"}
                            onChange={() => {
                              setSelectedPassPlanId(3);
                              handlePaymentMethodChange("multipleEntryPass");
                            }}
                          />
                        </div>
                      </div>
                      <div className="mobile-popup-payment-method-container">
                        <div className="mobile-popup-card-number">
                          <img src={CouponIcon} alt="Pass" style={{ width: "28px", height: "28px" }} />
                        </div>
                        <div className="radio-btn-container">
                          <p className="credit-card">כרטיסיית 4 כניסות</p>
                          <Radio
                            checked={selectedPassPlanId === 4 && selectedPaymentMethod !== "creditCard"}
                            onChange={() => {
                              setSelectedPassPlanId(4);
                              handlePaymentMethodChange("multipleEntryPass");
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {!isMaoflaamit && (!isClalit || (isClalit && !hasActiveMultipleEntryPass)) && (
                    <div className="mobile-popup-payment-method-container">
                      <div className="mobile-popup-card-number">
                        <span className="card-dots">●●●●</span>
                        <span className="card-number">{last4Digit}</span>
                        <img src={MastercardNew} alt="" className="card-logo" />
                      </div>
                      <div className="radio-btn-container">
                        <p className="credit-card">{t("CREDIT_CARD")}</p>
                        <Radio
                          checked={isMaoflaamit ? selectedPaymentMethod === "creditCard" : true}
                          onChange={isMaoflaamit ? () => handlePaymentMethodChange("creditCard") : undefined}
                        />
                      </div>
                    </div>
                  )}

                  <div className={isMaoflaamit ? "mobile-popup-text-footer-maof": "mobile-popup-text-footer"}>
                    <p className="mobile-popup-text-thin">{t("TOTAL_PRICE")}:</p>
                    {calculatePrice() > 0 && <p>₪{calculatePrice()}</p>}
                  </div>
                </div>
              </div>
            </div>
            <div className="mobile-popup-btn-block">
              <button
                onClick={() => handlePayment()}
                style={{ fontSize: "18px", fontWeight: 700, width: "100%" }}
                className={"main-page-btn big-button very-big-button lightred-btn"}
              >
                {t("PAYMENT")}
              </button>
              {/* {isClalit && <PoweredBy />} */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultipleEntryPassPurchase;
